import { createTheme, ThemeOptions } from "@mui/material";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    premium: true;
    secondary: true;
    secondarySelected: true;
    blue: true;
    darkBlue: true;
    notSelected: true;
    line: true;
    user: true;
    customText: true;
    customTextSelected: true;
    topAddButton: true;
    customBlueText: true;
    blueGradient: true;
    customBlueTextUnderLine: true;
    userAvatarMenuText: true;
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    bannerTitle: React.CSSProperties;
    bannerGradient: React.CSSProperties;
    bannerDescription: React.CSSProperties;
    sectionTitle: React.CSSProperties;
    whyTitle: React.CSSProperties;
    whyText: React.CSSProperties;
    communitySectionTitle: React.CSSProperties;
    shirtViewTitle: React.CSSProperties;
    shirtViewText: React.CSSProperties;
    userName: React.CSSProperties;
    statTitle: React.CSSProperties;
    blueBoldText: React.CSSProperties;
    statNumber: React.CSSProperties;
    followCardUserName: React.CSSProperties;
    featureText: React.CSSProperties;
    upgradeTitle: React.CSSProperties;
    upgradeSubtitle: React.CSSProperties;
    footerElement: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    bannerTitle?: React.CSSProperties;
    bannerGradient: React.CSSProperties;
    bannerDescription: React.CSSProperties;
    sectionTitle: React.CSSProperties;
    whyTitle: React.CSSProperties;
    whyText: React.CSSProperties;
    communitySectionTitle: React.CSSProperties;
    shirtViewTitle: React.CSSProperties;
    shirtViewText: React.CSSProperties;
    userName: React.CSSProperties;
    statTitle: React.CSSProperties;
    blueBoldText: React.CSSProperties;
    statNumber: React.CSSProperties;
    followCardUserName: React.CSSProperties;
    featureText: React.CSSProperties;
    upgradeTitle: React.CSSProperties;
    upgradeSubtitle: React.CSSProperties;
    footerElement: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    bannerTitle: true;
    bannerGradient: true;
    bannerDescription: true;
    sectionTitle: true;
    whyTitle: true;
    whyText: true;
    communitySectionTitle: true;
    shirtViewTitle: true;
    shirtViewText: true;
    userName: true;
    statTitle: true;
    blueBoldText: true;
    statNumber: true;
    followCardUserName: true;
    featureText: true;
    upgradeTitle: true;
    upgradeSubtitle: true;
    footerElement: true;
  }
}
let themeAux = createTheme();

export const themeDefinition: ThemeOptions = {
  palette: {
    primary: {
      main: "#000000",
      contrastText: "#FAFAFA",
    },
    secondary: {
      main: "#4663ff",
      contrastText: "#000000",
    },
  },
  typography: {
    fontFamily: "Inter Tight",
    body1: {
      fontFamily: "Inter Tight",
    },
    subtitle1: {
      fontFamily: "Inter Tight",
      fontWeight: "700 !important",
      fontSize: "1rem !important",
    },
    footerElement: {
      fontFamily: "Inter Tight",
      fontSize: "0.9rem !important",
      fontWeight: "500 !important",
      color: "black !important",
    },
    body2: {
      fontFamily: "Inter Tight",
    },
    h1: {
      fontFamily: "Inter Tight",
      fontSize: "2.5rem !important",
      fontWeight: "700 !important",
      lineHeight: "1.2 !important",
      [themeAux.breakpoints.down("md")]: {
        fontSize: "26px  !important",
      },
    },
    h2: {
      fontFamily: "Inter Tight",
      fontSize: "2rem !important",
      fontWeight: "600 !important",
      lineHeight: "1.3 !important",
    },
    h3: {
      fontFamily: "Inter Tight",
      fontSize: "2.35rem !important",
      fontWeight: "500 !important",
      lineHeight: "1.4 !important",
      textShadow: "0 1px 0 black",
      ["@media (max-width:992px)"]: {
        fontSize: "1.5rem !important",
      },
    },
    h4: {
      fontFamily: "Inter Tight",
      fontSize: "1.5rem !important",
      fontWeight: "500 !important",
      lineHeight: "1.5 !important",
    },
    h5: {
      fontFamily: "Inter Tight",
      fontSize: "1.25rem !important",
      fontWeight: "400 !important",
      lineHeight: "1.6 !important",
    },
    h6: {
      fontFamily: "Inter Tight",
      fontSize: "1rem !important",
      fontWeight: "400 !important",
      lineHeight: "1.7 !important",
    },
    bannerTitle: {
      fontFamily: "Inter Tight",
      fontWeight: "700 !important",
      color: "#3c3c40 !important",
      lineHeight: "1.2 !important",
      fontSize: "40px !important",
      fontStyle: "italic",
      [themeAux.breakpoints.down("md")]: {
        fontSize: "28px  !important",
      },
    },
    bannerGradient: {
      fontFamily: "Inter Tight",
      fontSize: "2.5rem !important",
      fontWeight: "700 !important",
      color: "#466bff !important",
      background: "-webkit-linear-gradient(#eee, #333)",
    },
    bannerDescription: {
      fontFamily: "Inter Tight",
      fontSize: "1.1rem !important",
      fontWeight: "500 !important",
      lineHeight: "1.6 !important",
      color: "#3c3c40 !important",
    },
    sectionTitle: {
      fontFamily: "Inter Tight",
      fontSize: "2.4rem !important",
      fontWeight: "800 !important",
      marginBottom: "2rem",
      color: "#3c3c40 !important",
      [themeAux.breakpoints.down("md")]: {
        fontSize: "26px  !important", // 20px
      },
    },
    whyTitle: {
      fontFamily: "Inter Tight",
      fontSize: "1.5rem !important",
      fontWeight: "700 !important",
      lineHeight: "1.2 !important",
      color: "#3c3c40 !important",
      textAlign: "center",
      [themeAux.breakpoints.down("md")]: {
        fontSize: "20px  !important",
      },
    },
    whyText: {
      fontFamily: "Inter Tight",
      fontSize: "0.9rem !important",
      fontWeight: "300 !important",
      lineHeight: "1.8 !important",
      color: "#3c3c40 !important",
      textAlign: "center",
    },
    communitySectionTitle: {
      fontFamily: "Inter Tight",
      fontSize: "1.75rem !important",
      fontWeight: "700 !important",
      lineHeight: "1.6 !important",
      color: "#3c3c40 !important",
      [themeAux.breakpoints.down("md")]: {
        fontSize: "1.2rem  !important",
      },
    },
    shirtViewTitle: {
      fontFamily: "Inter Tight",
      fontSize: "2rem !important",
      fontWeight: "800 !important",
      lineHeight: "1.6 !important",
      color: "#3c3c40 !important",
      marginBottom: "10px !important",
      marginTop: "10px !important",
    },
    shirtViewText: {
      fontFamily: "Inter Tight",
      fontSize: "1rem !important",
      fontWeight: "500 !important",
      lineHeight: "1.6 !important",
      color: "#000 !important",
      fontStyle: "italic !importnat",
    },
    blueBoldText: {
      fontFamily: "Inter Tight",
      fontSize: "1rem !important",
      fontWeight: "700 !important",
      lineHeight: "1.6 !important",
      color: "#4663ff !important",
      textDecoration: "none !important",
    },
    userName: {
      fontFamily: "Inter Tight",
      fontSize: "1.4rem !important",
      fontWeight: "800 !important",
      lineHeight: "1.6 !important",
      color: "#ffffff !important",
      textDecoration: "none !important",
      textAlign: "left",
    },
    followCardUserName: {
      fontFamily: "Inter Tight",
      fontSize: "1rem !important",
      fontWeight: "700 !important",
      lineHeight: "1.6 !important",
      color: "#3c3c40 !important",
      textDecoration: "none !important",
    },
    statTitle: {
      fontFamily: "Inter Tight",
      fontSize: "1.1rem !important",
      fontWeight: "700 !important",
      lineHeight: "1.6 !important",
      color: "#3c3c40 !important",
    },
    statNumber: {
      fontFamily: "Inter Tight",
      fontWeight: "700 !important",
      color: "#4663ff !important",
      textDecoration: "none !important",
    },
    featureText: {
      fontFamily: "Inter Tight",
      fontSize: "1rem !important",
      fontWeight: "400 !important",
      lineHeight: "1.6 !important",
      color: "#3c3c40 !important",
      marginBottom: "30px !important",
    },
    upgradeTitle: {
      fontFamily: "Inter Tight",
      fontSize: "1.5rem !important",
      fontWeight: "700 !important",
      lineHeight: "1.6 !important",
      color: "#3c3c40 !important",
      textAlign: "center",
    },
    upgradeSubtitle: {
      fontFamily: "Inter Tight",
      fontSize: "0.9rem !important",
      fontWeight: "400 !important",
      lineHeight: "1.6 !important",
      color: "#3c3c40 !important",
      textAlign: "center",
    },
  },
  components: {
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderBottom: "none",
          borderRadius: 8,
        },
        underline: {
          "&:before": {
            borderBottom: "none",
          },
          "&:after": {
            borderBottom: "none",
          },
          "&:hover:not(.Mui-disabled):before": {
            borderBottom: "none",
          },
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: "blueGradient" },
          style: {
            fontFamily: "Inter Tight",
            textTransform: "uppercase",
            backgroundColor: "#4695ff",
            color: "white",
            fontWeight: 700,
            fontSize: "1.2rem",
            background: "linear-gradient(60deg, #46BBFF 0%, #1F40DB 100%)",
          },
        },
        {
          props: { variant: "premium" },
          style: {
            fontFamily: "Inter Tight",
            textTransform: "uppercase",
            fontSize: "14px !important",
            fontWeight: "700 !important",
            background: "linear-gradient(60deg, #e6c305 0%, #f4ee32 100%)",
            color: "black",
            paddingTop: "10px !important",
            paddingBottom: "10px !important",
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            fontFamily: "Inter Tight",
            textTransform: "capitalize",
            backgroundColor: "#dfdfe2",
            color: "#000",
          },
        },
        {
          props: { variant: "secondarySelected" },
          style: {
            fontFamily: "Inter Tight",
            textTransform: "capitalize",
            backgroundColor: "#3c3c40",
            color: "#fff !important",
            cursor: "pointer Tight !important",
          },
        },
        {
          props: { variant: "blue" },
          style: {
            fontFamily: "Inter Tight",
            textTransform: "uppercase",
            backgroundColor: "#4663ff",
            color: "white",
            fontWeight: 700,
            fontSize: "1.2rem",
          },
        },
        {
          props: { variant: "darkBlue" },
          style: {
            fontFamily: "Inter Tight",
            textTransform: "capitalize",
            backgroundColor: "#4663ff",
            color: "white",
            borderRadius: "5px",
            fontWeight: 700,
            paddingLeft: "25px !important",
            paddingRight: "25px !important",
          },
        },
        {
          props: { variant: "notSelected" },
          style: {
            fontFamily: "Inter Tight",
            borderRadius: "5px",
            backgroundColor: "#dfdfe2",
            color: "#000",
            textTransform: "capitalize",
            paddingLeft: "15px !important",
            paddingRight: "15px !important",
          },
        },
        {
          props: { variant: "line" },
          style: {
            fontFamily: "Inter Tight",
            borderRadius: "5px",
            backgroundColor: "#ffffff",
            color: "black",
            textTransform: "capitalize",
            fontWeight: 700,
            border: "2px solid black",
            paddingLeft: "25px !important",
            paddingRight: "25px !important",
          },
        },
        {
          props: { variant: "user" },
          style: {
            fontFamily: "Inter Tight",
            fontSize: "0.75rem !important",
            borderRadius: "50px",
            backgroundColor: "#ffffff",
            color: "#4663ff",
            textTransform: "capitalize",
            fontWeight: 700,
            paddingLeft: "15px !important",
            paddingRight: "15px !important",
          },
        },
        {
          props: { variant: "customText" },
          style: {
            fontFamily: "Inter Tight",
            backgroundColor: "#ffffff",
            color: "#3c3c40",
            textTransform: "capitalize",
            fontWeight: 700,
            paddingLeft: "25px !important",
            paddingRight: "25px !important",
            fontSize: "1rem !important",
            [themeAux.breakpoints.down("sm")]: {
              fontSize: "0.85rem  !important",
              paddingLeft: "10px !important",
              paddingRight: "10px !important",
            },
          },
        },
        {
          props: { variant: "customTextSelected" },
          style: {
            fontFamily: "Inter Tight",
            backgroundColor: "#ffffff",
            color: "#4663ff !important",
            borderRadius: "5px",
            // border: "1px solid #3c3c40",
            textTransform: "capitalize",
            fontWeight: 700,
            paddingLeft: "25px !important",
            paddingRight: "25px !important",
            fontSize: "1rem !important",
            [themeAux.breakpoints.down("sm")]: {
              fontSize: "0.85rem  !important",
              paddingLeft: "10px !important",
              paddingRight: "10px !important",
            },
          },
        },
        {
          props: { variant: "customBlueText" },
          style: {
            fontFamily: "Inter Tight",
            backgroundColor: "#ffffff",
            color: "#4663ff !important",
            borderRadius: "5px",
            border: "2px solid #4663ff !important",
            textTransform: "uppercase",
            fontWeight: 700,
            paddingLeft: "25px !important",
            paddingRight: "25px !important",
            [themeAux.breakpoints.down("md")]: {
              fontSize: "0.75rem  !important", // 20px
              paddingLeft: "10px !important",
              paddingRight: "10px !important",
            },
          },
        },
        {
          props: { variant: "customBlueTextUnderLine" },
          style: {
            fontFamily: "Inter Tight",
            color: "#4663ff !important",
            fontWeight: "800 !important",
            borderRadius: "5px",
            border: "0px solid #4663ff !important",
            textDecoration: "underline",
            textTransform: "none",
            paddingLeft: "0px !important",
            paddingRight: "0px !important",
            fontSize: "1rem",
          },
        },
        {
          props: { variant: "topAddButton" },
          style: {
            fontFamily: "Inter Tight",
            backgroundColor: "#ffffff",
            color: "#bfbfbf",
            textTransform: "none",
            fontWeight: 500,
            paddingLeft: "25px !important",
            paddingRight: "25px !important",
            fontSize: "1.25rem  !important",
            [themeAux.breakpoints.down("md")]: {
              paddingLeft: "10px !important",
              paddingRight: "10px !important",
            },
          },
        },
      ],
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          bannerDescription: "p",
          whyTitle: "p",
          whyText: "p",
          shirtViewTitle: "h1",
          shirtViewText: "p",
        },
      },
    },
  },
};

export const theme = createTheme(themeAux, themeDefinition);
